import { Link } from "@tanstack/react-router";
import type { Counts } from "../api.js";
import { endpoints } from "../api.js";
import { useResource } from "../useResource.js";
import styles from "./Wall.module.css";

/**
 * `BEST ON DESKTOP` (design/README.md §13).
 *
 * *"Exactly four things work well; everything else gets a wall, not a bad small version."*
 *
 * The wall is not an apology and it is not a paywall — it is a statement that a rule builder with
 * nested groups, a 3,418-row table with eight columns, and a covers grid are desktop work, and
 * that squeezing them into 390px produces something that works only in the sense that it renders.
 * It says which screen you asked for, what does work here, and takes you to one of those.
 */

const WORKS = "RATE · SEARCH · RANKINGS · ALBUM";

export interface WallProps {
  /** The screen the user asked for, named as the rail names it. */
  screen: string;
  /** One sentence about why this one in particular is desktop work. */
  because: string;
  counts?: Counts | null;
}

export function Wall({ screen, because, counts = null }: WallProps) {
  return (
    <div className={styles.wall} data-testid="mobile-wall">
      <div className={styles.body}>
        <div className={styles.eyebrow} data-testid="wall-screen">
          {screen}
        </div>
        <div className={styles.title}>Best on desktop</div>
        <p className={styles.copy}>{because}</p>

        <div className={styles.works}>
          WORKING HERE:
          <br />
          {WORKS}
        </div>

        <div className={styles.actions}>
          <Link to="/rate" className={styles.primary} data-testid="wall-rate">
            {/* The count travels because "go and rate" is a different offer when 312 are waiting
                than when 3 are. A blank rather than a zero while it loads. */}
            GO TO RATE{counts ? ` · ${counts.needToRate.toLocaleString()} WAITING` : ""}
          </Link>
          <Link to="/rankings" className={styles.secondary} data-testid="wall-rankings">
            OPEN RANKINGS
          </Link>
        </div>
      </div>

      <div className={styles.footnote}>
        NO BOTTOM TAB BAR. NO SHEET MODALS.
        <br />
        NO PULL-TO-REFRESH. IT IS A WEB PAGE AND SAYS SO.
      </div>
    </div>
  );
}

/**
 * Renders the wall on a mobile viewport and the real screen otherwise.
 *
 * A gate rather than a per-screen media query, because "this screen is desktop work" is a decision
 * about the screen, not about its layout — and a screen that merely reflows badly below 900px
 * would still be reachable, which is the outcome §13 rules out.
 */
export function MobileWall({ screen, because }: { screen: string; because: string }) {
  const counts = useResource(() => endpoints.counts(), []);
  return <Wall screen={screen} because={because} counts={counts.data} />;
}
